import { useTranslation } from 'react-i18next';

import PillarAlignmentCard from './PillarAlignmentCard';

import type { PillarChoice } from '@/pages/Objectives/hooks/usePillarChoice';
import type { ObjectiveFormData } from '@/pages/Objectives/validation';
import type { Control } from 'react-hook-form';

import objectivesVisionArrow from '@/assets/illustrations/objectivesVisionArrow.png';
import objectivesVisionBadge from '@/assets/illustrations/objectivesVisionBadge.png';
import { SECTION_TITLE_TIGHT } from '@/components/objectives/objectiveStyles';

interface VisionAlignmentRowProps {
    /** The company's strategic vision; without one, the whole vision card and its arrow stay
     * out and the pillar choice takes the row. */
    strategicVision?: string;
    pillarChoice: PillarChoice;
    control: Control<ObjectiveFormData>;
}

/**
 * The form's opening row: the company vision on the left, the pillar choice on the right, and
 * the decorative arrow drawn between them. The column/row switch lives in `.obj-vision-row`
 * (`app.css`) because a base utility beats its own `lg:` variant in this build.
 */
export default function VisionAlignmentRow({
    strategicVision,
    pillarChoice,
    control,
}: VisionAlignmentRowProps) {
    const { t } = useTranslation();

    if (!strategicVision) {
        return (
            <div
                className={'obj-vision-row'}
                data-testid={'objective-form-vision-row'}
            >
                <PillarAlignmentCard
                    pillarChoice={pillarChoice}
                    control={control}
                    layout={'full-width'}
                />
            </div>
        );
    }

    return (
        <div className={'obj-vision-frame'}>
            <div
                className={'obj-vision-row'}
                data-testid={'objective-form-vision-row'}
            >
                <div
                    className={'obj-vision-card'}
                    data-testid={'objective-form-vision-card'}
                >
                    <div
                        className={
                            'rounded-[12px] border border-[rgba(238,251,255,1)] bg-white/45 p-[12px_55px_26px_55px] backdrop-blur-[10px]'
                        }
                    >
                        <div className={'flex flex-col gap-2'}>
                            <div className={'flex items-center gap-4'}>
                                {/* Intrinsic size of the PNG itself (224x234): height fixed, width left
                                at its natural ratio so the badge is never squeezed narrower. */}
                                <img
                                    src={objectivesVisionBadge}
                                    alt={''}
                                    aria-hidden
                                    width={224}
                                    height={234}
                                    className={'h-[156px] w-auto flex-shrink-0'}
                                />

                                <h3 className={SECTION_TITLE_TIGHT}>
                                    {t('objectives.form.companyVision')}
                                </h3>
                            </div>

                            <div
                                className={
                                    'flex w-full items-center justify-center rounded-lg bg-white/90 p-[55px_52px]'
                                }
                            >
                                <p
                                    className={
                                        'font-handwriting text-navy text-center text-[40px] leading-[47px] tracking-[-0.8px]'
                                    }
                                >
                                    {strategicVision}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <img
                    src={objectivesVisionArrow}
                    alt={''}
                    aria-hidden
                    className={'obj-vision-arrow'}
                    data-testid={'objective-form-vision-arrow'}
                />

                <PillarAlignmentCard
                    pillarChoice={pillarChoice}
                    control={control}
                />
            </div>
        </div>
    );
}
